import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { useSubscription } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowLeft, Mail, Phone, MapPin, PawPrint, Calendar, Weight, Eye, Plus, Upload, X, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, validateImageFile } from '@/utils/imageCompression';
import { uploadClinicImage } from '@/utils/cloudinary';

export const OwnerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [owner, setOwner] = useState(null);
  const [pets, setPets] = useState([]);
  const [petTypes, setPetTypes] = useState([]);
  const { getPetsPerOwnerLimit, canAddPetForOwner: subCanAddPetForOwner } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [isPetDialogOpen, setIsPetDialogOpen] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingPetId, setDeletingPetId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [petFormData, setPetFormData] = useState({
    name: '',
    date_of_birth: '',
    pet_type_id: '',
    owner_id: '',
    image_url: null,
    breed: '',
    gender: '',
    weight: '',
    color: '',
  });

  const fetchOwnerPets = async () => {
    try {
      const res = await api.get('/pets');
      setPets(res.data.filter((p) => p.owner_id === id));
    } catch (error) {
      toast.error('Failed to reload pets');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ownersRes, petsRes, petTypesRes] = await Promise.all([
          api.get('/owners'),
          api.get('/pets'),
          api.get('/pet-types'),
        ]);

        const foundOwner = ownersRes.data.find((o) => o.id === id);
        if (!foundOwner) {
          toast.error('Owner not found');
          navigate('/dashboard/owners');
          return;
        }

        setOwner(foundOwner);
        setPets(petsRes.data.filter((p) => p.owner_id === id));
        setPetTypes(petTypesRes.data);
      } catch (error) {
        toast.error('Failed to load owner details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const canAddPet = () => subCanAddPetForOwner(pets.length);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateImageFile(file, 3);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    try {
      toast.info('Compressing image...');
      const compressed = await compressImage(file, { maxSizeMB: 3, maxWidthOrHeight: 1920, quality: 0.8 });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(compressed);
      setImageFile(compressed);
      toast.success(`Image compressed to ${(compressed.size / (1024 * 1024)).toFixed(2)}MB`);
    } catch (error) {
      toast.error('Failed to process image: ' + error.message);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setPetFormData((prev) => ({ ...prev, image_url: null }));
  };

  const resetPetForm = () => {
    setPetFormData({ name: '', date_of_birth: '', pet_type_id: '', owner_id: id, image_url: null, breed: '', gender: '', weight: '', color: '' });
    setImageFile(null);
    setImagePreview(null);
    setEditingPet(null);
  };

  const handlePetSubmit = async (e) => {
    e.preventDefault();
    if (!editingPet && !canAddPet()) {
      toast.error(`This owner has reached the limit of ${getPetsPerOwnerLimit()} pets. Please upgrade your subscription.`);
      return;
    }
    setUploading(true);
    try {
      let imageUrl = petFormData.image_url;
      if (imageFile) {
        toast.info('Uploading image...');
        imageUrl = await uploadClinicImage(imageFile, 'pets');
        toast.success('Image uploaded successfully');
      }
      const payload = {
        ...petFormData,
        owner_id: id,
        image_url: imageUrl || null,
        weight: petFormData.weight !== '' ? parseFloat(petFormData.weight) : null,
        breed: petFormData.breed || null,
        gender: petFormData.gender || null,
        color: petFormData.color || null,
      };
      if (editingPet) {
        await api.put(`/pets/${editingPet.id}`, payload);
        toast.success('Pet updated successfully');
      } else {
        await api.post('/pets', payload);
        toast.success('Pet added successfully');
      }
      setIsPetDialogOpen(false);
      resetPetForm();
      await fetchOwnerPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || (editingPet ? 'Failed to update pet' : 'Failed to add pet'));
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setPetFormData({
      name: pet.name,
      date_of_birth: pet.date_of_birth || '',
      pet_type_id: pet.pet_type_id,
      owner_id: pet.owner_id,
      image_url: pet.image_url,
      breed: pet.breed || '',
      gender: pet.gender || '',
      weight: pet.weight || '',
      color: pet.color || '',
    });
    setImageFile(null);
    setImagePreview(pet.image_url || null);
    setIsPetDialogOpen(true);
  };

  const handleDelete = (petId) => {
    setDeletingPetId(petId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/pets/${deletingPetId}`);
      toast.success('Pet deleted successfully');
      await fetchOwnerPets();
    } catch (error) {
      toast.error('Failed to delete pet');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingPetId(null);
    }
  };

  const getPetTypeName = (petTypeId) => {
    return petTypes.find((pt) => pt.id === petTypeId)?.name || 'Unknown';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calcAge = (dob) => {
    if (!dob) return null;
    const diff = Date.now() - new Date(dob).getTime();
    const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    if (years === 0) {
      const months = Math.floor(diff / (1000 * 60 * 60 * 24 * 30.44));
      return `${months} mo`;
    }
    return `${years} yr${years !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!owner) return null;

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto" data-testid="owner-view-page">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard/owners')}
        className="mb-6 text-sage-700 hover:text-sage-900 hover:bg-sage-50 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Owners
      </Button>

      {/* Owner Card */}
      <div className="bg-white rounded-3xl shadow-lg p-5 md:p-8 mb-8 border border-stone-100">
        <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-sage-100 flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-sage-600">
              {owner.full_name?.charAt(0)?.toUpperCase()}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-3xl font-heading font-bold text-sage-800 mb-1">
              {owner.full_name}
            </h1>
            <p className="text-gray-500 mb-4">Pet Owner</p>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-sage-500 flex-shrink-0" />
                <span className="text-sm truncate">{owner.email || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-sage-500 flex-shrink-0" />
                <span className="text-sm">{owner.phone || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-sage-500 flex-shrink-0" />
                <span className="text-sm truncate">{owner.address || '—'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pets Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PawPrint className="w-5 h-5 text-sage-600" />
            <h2 className="text-2xl font-heading font-bold text-sage-800">
              Pets
            </h2>
            <span className="ml-2 bg-sage-100 text-sage-700 text-sm font-semibold px-3 py-0.5 rounded-full">
              {pets.length}
            </span>
          </div>
          <div className="flex gap-2">
            <Dialog open={isPetDialogOpen} onOpenChange={(open) => { setIsPetDialogOpen(open); if (!open) resetPetForm(); }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow"
                  size="sm"
                  onClick={resetPetForm}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Pet
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPet ? 'Edit Pet' : 'Add New Pet for ' + owner?.full_name}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePetSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="pet-name">Pet Name</Label>
                    <Input
                      id="pet-name"
                      value={petFormData.name}
                      onChange={(e) => setPetFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="rounded-xl mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pet-type">Pet Type</Label>
                    <Select value={petFormData.pet_type_id} onValueChange={(value) => setPetFormData((prev) => ({ ...prev, pet_type_id: value }))}>
                      <SelectTrigger className="rounded-xl mt-2">
                        <SelectValue placeholder="Select pet type" />
                      </SelectTrigger>
                      <SelectContent>
                        {petTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pet-dob">Date of Birth</Label>
                    <Input
                      id="pet-dob"
                      type="date"
                      value={petFormData.date_of_birth}
                      onChange={(e) => setPetFormData((prev) => ({ ...prev, date_of_birth: e.target.value }))}
                      className="rounded-xl mt-2"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pet-breed">Breed (optional)</Label>
                    <Input
                      id="pet-breed"
                      value={petFormData.breed}
                      onChange={(e) => setPetFormData((prev) => ({ ...prev, breed: e.target.value }))}
                      className="rounded-xl mt-2"
                      placeholder="e.g. Labrador Retriever"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pet-gender">Gender</Label>
                    <Select value={petFormData.gender} onValueChange={(value) => setPetFormData((prev) => ({ ...prev, gender: value }))}>
                      <SelectTrigger className="rounded-xl mt-2">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Unknown">Unknown</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pet-weight">Weight (kg, optional)</Label>
                      <Input
                        id="pet-weight"
                        type="number"
                        min="0"
                        step="0.1"
                        value={petFormData.weight}
                        onChange={(e) => setPetFormData((prev) => ({ ...prev, weight: e.target.value }))}
                        className="rounded-xl mt-2"
                        placeholder="e.g. 5.2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pet-color">Color (optional)</Label>
                      <Input
                        id="pet-color"
                        value={petFormData.color}
                        onChange={(e) => setPetFormData((prev) => ({ ...prev, color: e.target.value }))}
                        className="rounded-xl mt-2"
                        placeholder="e.g. Golden"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Photo (optional)</Label>
                    <p className="text-xs text-gray-500 mt-1 mb-2">Max 3MB. Image will be automatically compressed.</p>
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl border-2 border-sage-200" />
                        <Button type="button" size="sm" variant="destructive" onClick={handleRemoveImage} className="absolute top-2 right-2 rounded-full">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-sage-400 transition-colors">
                        <input id="add-pet-image" type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageChange} className="hidden" />
                        <label htmlFor="add-pet-image" className="cursor-pointer">
                          <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                          <p className="text-sm font-medium text-gray-700">Click to upload photo</p>
                          <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP (max 3MB)</p>
                        </label>
                      </div>
                    )}
                  </div>
                  {!editingPet && !canAddPet() && (
                    <p className="text-sm text-red-600">This owner has reached the pet limit ({getPetsPerOwnerLimit()} pets). Please upgrade your subscription.</p>
                  )}
                  <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" disabled={uploading || (!editingPet && !canAddPet())}>
                    {uploading ? 'Uploading...' : editingPet ? 'Update Pet' : 'Add Pet'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <Button
              className="bg-white hover:bg-sage-50 text-sage-700 border border-sage-200 rounded-full shadow"
              onClick={() => navigate('/dashboard/pets')}
              size="sm"
            >
              Manage Pets
            </Button>
          </div>
        </div>

        {pets.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-12 text-center">
            <PawPrint className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No pets registered for this owner yet.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pets.map((pet) => (
              <div
                key={pet.id}
                className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 hover:shadow-md transition-shadow"
                data-testid={`pet-card-${pet.id}`}
              >
                {/* Pet image or icon */}
                <div className="w-14 h-14 rounded-xl bg-sage-50 flex items-center justify-center mb-4 overflow-hidden">
                  {pet.image_url ? (
                    <img
                      src={pet.image_url}
                      alt={pet.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <PawPrint className="w-7 h-7 text-sage-400" />
                  )}
                </div>

                <h3 className="text-lg font-semibold text-gray-800 mb-1">{pet.name}</h3>

                <span className="inline-block bg-sage-100 text-sage-700 text-xs font-medium px-2.5 py-1 rounded-full mb-4">
                  {getPetTypeName(pet.pet_type_id)}
                </span>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span>{formatDate(pet.date_of_birth)}</span>
                    {pet.date_of_birth && (
                      <span className="text-sage-600 font-medium">
                        ({calcAge(pet.date_of_birth)})
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mb-2 mt-2">
                  <Button
                    size="sm"
                    className="flex-1 rounded-full bg-sage-500 hover:bg-sage-600 text-white"
                    onClick={() => navigate(`/dashboard/pets/${pet.id}?from=owner`)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-sage-200 text-sage-700 hover:bg-sage-50"
                    onClick={() => handleEdit(pet)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-red-200 text-red-500 hover:bg-red-50"
                    onClick={() => handleDelete(pet.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Pet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this pet? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
